<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'

const props = defineProps({
    leadId: {
        type: [Number, String],
        required: true
    }
})

// Like Task due times, enabling a time starts it at 09:00 rather than midnight.
const DEFAULT_REMINDER_TIME = '09:00'

const createEmptyAction = () => ({
    action: '',
    date: null,
    time: null,
    prio: '1'
})

// A new action starts on today's date; midnight matches the dates built when editing.
const createNewAction = () => {
    const now = new Date()

    return { ...createEmptyAction(), date: new Date(now.getFullYear(), now.getMonth(), now.getDate()) }
}

const timeEnabled = ref(false)
const working = ref(false)
const loadingActions = ref(false)
const localActions = ref([])
const editingActionId = ref(null)
const editTimeEnabled = ref(false)
const updatingAction = ref(false)
const notification = reactive({
    type: '',
    message: ''
})
const creatingAction = reactive(createNewAction())
const editingAction = reactive(createEmptyAction())

const priorityOptions = [
    { value: 1, label: `1 (${_mft('shared:priority.high')})` },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: `5 (${_mft('shared:priority.low')})` }
]

const showNotification = computed(() => notification.message.length > 0)
const submitIsDisabled = computed(() => {
    return creatingAction.action.length === 0
        || creatingAction.date === null
        || (timeEnabled.value && !creatingAction.time)
        || creatingAction.prio === ''
})
const editSubmitIsDisabled = computed(() => {
    return editingAction.action.length === 0
        || editingAction.date === null
        || (editTimeEnabled.value && !editingAction.time)
        || editingAction.prio === ''
})

const closeNotification = () => {
    notification.message = ''
}

const resetCreateActionForm = () => {
    Object.assign(creatingAction, createNewAction())
    timeEnabled.value = false
}

const resetEditActionForm = () => {
    Object.assign(editingAction, createEmptyAction())
    editingActionId.value = null
    editTimeEnabled.value = false
}

const pad = value => String(value).padStart(2, '0')

// The time is wall-clock; the server converts it to UTC with the owner's profile time zone.
const buildActionPayload = (action, timeIsEnabled) => {
    const payload = {
        action: action.action,
        date: `${action.date.getFullYear()}-${pad(action.date.getMonth() + 1)}-${pad(action.date.getDate())}`,
        prio: action.prio
    }

    if (timeIsEnabled && action.time) {
        payload.time = action.time
    }

    return payload
}

// The toggle also echoes changes made from code, such as opening an action for editing,
// so only a switch from off to on is the user enabling the time.
const setTimeEnabled = enabled => {
    if (enabled && !timeEnabled.value) {
        creatingAction.time = DEFAULT_REMINDER_TIME
    }

    timeEnabled.value = enabled
}

const setEditTimeEnabled = enabled => {
    if (enabled && !editTimeEnabled.value) {
        editingAction.time = DEFAULT_REMINDER_TIME
    }

    editTimeEnabled.value = enabled
}

const loadActions = async () => {
    if (!props.leadId) {
        localActions.value = []

        return
    }

    loadingActions.value = true

    try {
        const res = await axios.get(route('bo.api.v1.crm.lead.actions', props.leadId))
        localActions.value = res.data.actions ?? []
    } catch (error) {
        notification.type = 'error'
        notification.message = error?.response?.data?.message || 'Failed to load actions'
        localActions.value = []
    } finally {
        loadingActions.value = false
    }
}

const createAction = async () => {
    if (submitIsDisabled.value || working.value) {
        return
    }

    working.value = true

    try {
        const payload = buildActionPayload(creatingAction, timeEnabled.value)

        const res = await axios.post(route('crm.leads.action.create', props.leadId), payload)

        notification.type = res.data.notification?.type || ''
        notification.message = res.data.notification?.message || ''

        resetCreateActionForm()
        await loadActions()
    } catch (error) {
        notification.type = 'error'
        notification.message = error?.response?.data?.message || 'Failed to create action'
    } finally {
        working.value = false
    }
}

const startEditingAction = action => {
    const [year, month, day] = action.local_date.split('-').map(Number)

    editingActionId.value = action.id
    editTimeEnabled.value = action.local_time !== null

    Object.assign(editingAction, {
        action: action.action ?? '',
        date: new Date(year, month - 1, day),
        time: action.local_time,
        prio: String(action.prio ?? '')
    })
}

const updateAction = async () => {
    if (editSubmitIsDisabled.value || updatingAction.value || editingActionId.value === null) {
        return
    }

    updatingAction.value = true

    try {
        const res = await axios.put(
            route('bo.api.v1.crm.lead.action.update', {
                id: props.leadId,
                action_id: editingActionId.value
            }),
            buildActionPayload(editingAction, editTimeEnabled.value)
        )

        notification.type = 'success'
        notification.message = res.data.message || 'Action updated'

        resetEditActionForm()
        await loadActions()
    } catch (error) {
        notification.type = 'error'
        notification.message = error?.response?.data?.message || 'Failed to update action'
    } finally {
        updatingAction.value = false
    }
}

onMounted(() => {
    loadActions()
})

watch(
    () => props.leadId,
    () => {
        resetCreateActionForm()
        resetEditActionForm()
        loadActions()
    }
)
</script>

<template>
    <notification
        :show="showNotification"
        @close="closeNotification()"
    >
        <template #header>
            {{ notification.message }}
        </template>
    </notification>
    <card-form-content>
        <template #header>
            <h2>{{ _mft('crm:leadActionCard.domainNamePlural') }}</h2>
        </template>
        <template #form>
            <form @submit.prevent="createAction">
                <input-text
                    v-model:content="creatingAction.action"
                    :label="_mft('crm:leadActionCard.title')"
                    required="required"
                    :content="creatingAction.action"
                    name="action"
                />

                <div class="mb-4">
                    <input-label
                        :label="_mft('crm:leadActionCard.date')"
                        required
                    />
                    <input-datepicker
                        :date="creatingAction.date"
                        :show-inline="false"
                        :min-date="null"
                        text-input
                        class="mt-1"
                        @update:date="creatingAction.date = $event"
                    />
                </div>

                <div>
                    <toggle-switch
                        label="crm:leadActionCard.reminderAtTime"
                        :content="timeEnabled"
                        @update:content="setTimeEnabled"
                    />
                </div>
                <input-timepicker
                    v-if="timeEnabled"
                    v-model:time="creatingAction.time"
                    text-input
                    required
                    class="-mt-2 mb-4"
                />

                <input-select
                    v-model:content="creatingAction.prio"
                    :options="priorityOptions"
                    :label="_mft('crm:leadActionCard.priority')"
                    required
                    :placeholder="_mft('crm:leadActionCard.priority')"
                    name="prio"
                />

                <div class="mt-2 flex justify-end">
                    <button-success
                        type="submit"
                        :busy="working"
                        :disabled="submitIsDisabled"
                        :class="submitIsDisabled ? 'cursor-not-allowed' : ''"
                    >
                        {{ _mft('shared:action.save') }}
                    </button-success>
                </div>
            </form>
        </template>
        <template #content>
            <div v-if="loadingActions" class="py-2">
                <mf-spinner-medium class="h-6 w-12"></mf-spinner-medium>
            </div>
            <p
                v-else-if="localActions.length === 0"
                class=""
            >
                {{ _mft('crm:leadActionCard.noRemindersAdded') }}
            </p>
            <div v-else>
                <ul
                    role="list"
                    class=""
                >
                    <li
                        v-for="action in localActions"
                        :key="action.id"
                        class="col-span-1 mt-1"
                    >
                        <card-sub
                            v-if="editingActionId === action.id"
                            class="mt-8 mb-6"
                        >
                            <form
                                @submit.prevent="updateAction"
                            >
                                <input-text
                                    v-model:content="editingAction.action"
                                    :label="_mft('crm:leadActionCard.title')"
                                    required="required"
                                    :content="editingAction.action"
                                    name="edit_action"
                                />

                                <div class="mb-4">
                                    <input-label
                                        :label="_mft('crm:leadActionCard.date')"
                                        required
                                    />
                                    <input-datepicker
                                        :date="editingAction.date"
                                        :show-inline="false"
                                        :min-date="null"
                                        text-input
                                        class="mt-1"
                                        @update:date="editingAction.date = $event"
                                    />
                                </div>

                                <div>
                                    <toggle-switch
                                        label="crm:leadActionCard.reminderAtTime"
                                        :content="editTimeEnabled"
                                        @update:content="setEditTimeEnabled"
                                    />
                                </div>
                                <input-timepicker
                                    v-if="editTimeEnabled"
                                    v-model:time="editingAction.time"
                                    text-input
                                    required
                                    class="mb-4"
                                />

                                <input-select
                                    v-model:content="editingAction.prio"
                                    :options="priorityOptions"
                                    :label="_mft('crm:leadActionCard.priority')"
                                    required
                                    :placeholder="_mft('crm:leadActionCard.priority')"
                                    name="edit_prio"
                                />

                                <div class="mt-2 flex justify-end space-x-2">
                                    <button-secondary
                                        type="button"
                                        @click="resetEditActionForm"
                                    >
                                        {{ _mft('shared:action.cancel') }}
                                    </button-secondary>
                                    <button-success
                                        type="submit"
                                        :busy="updatingAction"
                                        :disabled="editSubmitIsDisabled"
                                        :class="editSubmitIsDisabled ? 'cursor-not-allowed' : ''"
                                    >
                                        {{ _mft('shared:action.update') }}
                                    </button-success>
                                </div>
                            </form>
                        </card-sub>
                        <crm-lead-action-item
                            v-else
                            :item="action"
                            :owner="action.user_info_rel"
                            @reload-items="loadActions"
                            @edit-action="startEditingAction"
                        />
                    </li>
                </ul>
            </div>
        </template>
    </card-form-content>
</template>
